import { call, put, takeLatest } from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';
import request from '@/utils/request';
import API from '@/config/api';
import type { SurveyTemplate } from '@/types/survey.types';
import {
  fetchTemplatesRequest, fetchTemplatesSuccess, fetchTemplatesFailure,
  fetchReorderListRequest, fetchReorderListSuccess, fetchReorderListFailure,
  reorderTemplatesRequest, reorderTemplatesSuccess, reorderTemplatesFailure,
  addTemplateRequest, addTemplateSuccess, addTemplateFailure,
  updateTemplateRequest, updateTemplateSuccess, updateTemplateFailure,
  deleteTemplateRequest, deleteTemplateSuccess, deleteTemplateFailure,
  fetchTemplateTagsRequest, fetchTemplateTagsSuccess, fetchTemplateTagsFailure,
  addTemplateTagRequest, addTemplateTagSuccess, addTemplateTagFailure,
  fetchEditionListRequest, fetchEditionListSuccess, fetchEditionListFailure,
  fetchThumbnailListRequest, fetchThumbnailListSuccess, fetchThumbnailListFailure,
} from './surveysSlice';

function* handleFetchTemplates(action: PayloadAction<{ page: number; limit: number; search?: string; sortField?: string; sortOrder?: string; status?: string; category?: string }>) {
  try {
    const res: { data: { data?: { surveyTemplatesMap?: SurveyTemplate[]; total?: number; list?: SurveyTemplate[]; totalRecords?: number }; list?: SurveyTemplate[]; totalRecords?: number } } = yield call(request.get, API.SURVEY_TEMPLATES, { params: action.payload });
    
    // API returns data.surveyTemplatesMap and data.total
    const list = res.data.data?.surveyTemplatesMap || res.data.data?.list || res.data.list || [];
    const total = res.data.data?.total || res.data.data?.totalRecords || res.data.totalRecords || 0;
    
    yield put(fetchTemplatesSuccess({ list, total }));
  } catch (e: unknown) {
    console.error('handleFetchTemplates error:', e);
    yield put(fetchTemplatesFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchReorderList() {
  try {
    const res: { data: { data: SurveyTemplate[] } } = yield call(request.get, API.TEMPLATE_ORDER_LIST);
    yield put(fetchReorderListSuccess(res.data.data));
  } catch (e: unknown) {
    console.error('handleFetchReorderList error:', e);
    yield put(fetchReorderListFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleReorderTemplates(action: PayloadAction<{ list: { _id: string; order: number }[] }>) {
  try {
    yield call(request.post, API.REORDER_TEMPLATES, action.payload);
    yield put(reorderTemplatesSuccess());
  } catch (e: unknown) {
    console.error('handleReorderTemplates error:', e);
    yield put(reorderTemplatesFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleAddTemplate(action: PayloadAction<Record<string, unknown>>) {
  try {
    yield call(request.post, API.ADD_TEMPLATE, action.payload);
    yield put(addTemplateSuccess());
  } catch (e: unknown) {
    console.error('handleAddTemplate error:', e);
    yield put(addTemplateFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleUpdateTemplate(action: PayloadAction<Record<string, unknown>>) {
  try {
    yield call(request.post, API.UPDATE_TEMPLATE, action.payload);
    yield put(updateTemplateSuccess());
  } catch (e: unknown) {
    console.error('handleUpdateTemplate error:', e);
    yield put(updateTemplateFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleDeleteTemplate(action: PayloadAction<string>) {
  try {
    yield call(request.delete, API.DELETE_TEMPLATE(action.payload));
    yield put(deleteTemplateSuccess(action.payload));
  } catch (e: unknown) {
    console.error('handleDeleteTemplate error:', e);
    yield put(deleteTemplateFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchTemplateTags() {
  try {
    const res: { data: { data: string[] } } = yield call(request.get, API.TEMPLATE_TAGS);
    yield put(fetchTemplateTagsSuccess(res.data.data));
  } catch (e: unknown) {
    console.error('handleFetchTemplateTags error:', e);
    yield put(fetchTemplateTagsFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleAddTemplateTag(action: PayloadAction<string>) {
  try {
    yield call(request.post, API.ADD_TEMPLATE_TAG, { name: action.payload });
    yield put(addTemplateTagSuccess(action.payload));
  } catch (e: unknown) {
    console.error('handleAddTemplateTag error:', e);
    yield put(addTemplateTagFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchEditionList() {
  try {
    const res: { data: { data: string[] } } = yield call(request.get, API.EDITION_LIST);
    yield put(fetchEditionListSuccess(res.data.data));
  } catch (e: unknown) {
    console.error('handleFetchEditionList error:', e);
    yield put(fetchEditionListFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

function* handleFetchThumbnailList() {
  try {
    const res: { data: { data?: { list: Array<{ _id: string; name: string; type: string; svgCode: string; svgTemplate: string; isActive: boolean; isDeleted: boolean }>; totalRecords: number } | string[]; list?: Array<{ _id: string; name: string; type: string; svgCode: string; svgTemplate: string; isActive: boolean; isDeleted: boolean }> } } = yield call(request.get, API.THUMBNAIL_LIST);
    // Handle different response structures
    let thumbnails: string[] = [];
    if (res.data.data) {
      if (Array.isArray(res.data.data)) {
        // Direct array of strings
        thumbnails = res.data.data;
      } else if (res.data.data.list) {
        // Object with list property - extract names
        thumbnails = res.data.data.list.map((t) => t.name);
      }
    } else if (res.data.list) {
      // Direct list in data
      thumbnails = res.data.list.map((t) => t.name);
    }
    yield put(fetchThumbnailListSuccess(thumbnails));
  } catch (e: unknown) {
    console.error('handleFetchThumbnailList error:', e);
    yield put(fetchThumbnailListFailure(e instanceof Error ? e.message : 'Failed'));
  }
}

export default function* surveysSaga() {
  yield takeLatest(fetchTemplatesRequest.type, handleFetchTemplates);
  yield takeLatest(fetchReorderListRequest.type, handleFetchReorderList);
  yield takeLatest(reorderTemplatesRequest.type, handleReorderTemplates);
  yield takeLatest(addTemplateRequest.type, handleAddTemplate);
  yield takeLatest(updateTemplateRequest.type, handleUpdateTemplate);
  yield takeLatest(deleteTemplateRequest.type, handleDeleteTemplate);
  yield takeLatest(fetchTemplateTagsRequest.type, handleFetchTemplateTags);
  yield takeLatest(addTemplateTagRequest.type, handleAddTemplateTag);
  yield takeLatest(fetchEditionListRequest.type, handleFetchEditionList);
  yield takeLatest(fetchThumbnailListRequest.type, handleFetchThumbnailList);
}
